import { ChatApiResponse } from "@/store/types/aichat";

export const streamChatAPI = async (options: {
  userMessage: string;
  messages?: {
    type: "user" | "assistant";
    content: string;
    timestamp?: string;
  }[];
  isAnonymous?: boolean;
  userInfo?: {
    email?: string | null;
    phoneNumber?: string | null;
    userId?: string | number | null;
  };
  eligibilityData?: any;
  activeFlow?: string | null;
  flowStep?: string | number | null;
  courseSlugs?: string[];
  onDelta: (delta: string) => void;
  onComplete: (data: ChatApiResponse) => void;
  onError: (error: string) => void;
}) => {
  const {
    userMessage,
    messages = [],
    isAnonymous,
    userInfo,
    eligibilityData,
    activeFlow,
    flowStep,
    courseSlugs,
    onDelta,
    onComplete,
    onError,
  } = options;

  let timerId: NodeJS.Timeout | null = null;
  let targetText = "";
  let displayedText = "";
  let isStreamFinished = false;
  let finalApiResponse: ChatApiResponse | null = null;
  let gotComplete = false;

  const startTypewriter = () => {
    if (timerId) return;

    timerId = setInterval(() => {
      if (displayedText.length < targetText.length) {
        const lag = targetText.length - displayedText.length;
        // Adjust step size dynamically to catch up if lagging too much
        const step = lag > 150 ? 8 : lag > 60 ? 4 : lag > 20 ? 2 : 1;
        displayedText += targetText.slice(displayedText.length, displayedText.length + step);
        onDelta(displayedText);
      } else if (isStreamFinished) {
        if (timerId) {
          clearInterval(timerId);
          timerId = null;
        }
        if (finalApiResponse) {
          onComplete({
            ...finalApiResponse,
            message: finalApiResponse.message || displayedText,
          });
        } else {
          onComplete({
            messages_received: 1,
            message: displayedText,
            querydb: false,
            embeddingsquery: "",
            search_results: [],
          });
        }
      }
    }, 40); // 40ms interval is very smooth (25 characters per second)
  };

  try {
    const conversationHistory = messages.map((msg, index) => ({
      role: msg.type === "user" ? "user" : "assistant",
      content: `${isAnonymous ? "The user is not logged in or signed up yet." : "The user is logged in already."} ${msg.content}`,
      timestamp: new Date().toISOString(),
      message_id: `${Date.now()}_${index}`,
    }));

    conversationHistory.push({
      role: "user",
      content: `${isAnonymous ? "The user is not logged in or signed up yet." : "The user is logged in already."} ${userMessage.trim()}`,
      timestamp: new Date().toISOString(),
      message_id: `${Date.now()}_current`,
    });

    const defaultEligibility = {
      targetDegree: "",
      targetCountry: "",
      targetIntake: "",
      targetCourse: "",
      previousCourse: "",
      previousDegree: "",
      previousCgpa: "",
    };

    const requestBody: any = {
      messages: conversationHistory,
      flow_state: {
        active_flow: activeFlow || undefined,
        step: flowStep || undefined,
        eligibility: eligibilityData || defaultEligibility,
      },
    };

    if (userInfo) {
      if (userInfo.email) requestBody.email = userInfo.email;
      if (userInfo.phoneNumber) requestBody.phoneNumber = userInfo.phoneNumber;
      if (userInfo.userId !== undefined && userInfo.userId !== null && userInfo.userId !== "") {
        requestBody.userId = userInfo.userId;
      }
    }

    if (courseSlugs && courseSlugs.length > 0) {
      requestBody.course_slugs = courseSlugs;
    }

    const response = await fetch("/api/chat", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errData = await response.json();
      throw new Error(errData.detail || "Failed to fetch chat stream");
    }

    if (!response.body) {
      throw new Error("No response body");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || ""; // save unfinished line

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        try {
          const parsed = JSON.parse(trimmed);
          if (parsed.type === "message_delta") {
            // Only stream/accumulate if delta has content
            if (typeof parsed.delta === "string") {
              targetText += parsed.delta;
              startTypewriter();
            }
          } else if (parsed.type === "message_complete" || parsed.type === "completed") {
            gotComplete = true;
            finalApiResponse = {
              messages_received: parsed.message ? 1 : 0,
              message: parsed.message || targetText,
              querydb: parsed.querydb || false,
              embeddingsquery: parsed.embeddingsquery || "",
              search_results: parsed.search_results || [],
              preferences: parsed.preferences || undefined,
              flow_state: parsed.flow_state || undefined,
              ui_type: parsed.ui_type || undefined,
              fields: parsed.fields || undefined,
              dynamic_options: parsed.dynamic_options || undefined,
              ctas: parsed.ctas || undefined,
              follow_up_questions: parsed.follow_up_questions || undefined,
            };
            isStreamFinished = true;
          } else if (parsed.message) {
            // Direct standard response (fallback to "same as previous" layout)
            gotComplete = true;
            onComplete(parsed);
          }
        } catch (parseError) {
          // If we fail to parse as JSON, check if it's a direct JSON string (fallback)
          console.error("Error parsing stream line:", trimmed, parseError);
        }
      }
    }

    // Set finished to true to let the typewriter finish typing remaining text
    isStreamFinished = true;
    startTypewriter(); // run typewriter to clean up if not already running

    // If stream ended but we got absolutely no delta/complete but have buffer
    if (!gotComplete && !targetText && buffer) {
      try {
        const parsed = JSON.parse(buffer);
        if (parsed.message) {
          onComplete(parsed);
        }
      } catch {
        // Not JSON
      }
    }
  } catch (error: any) {
    console.error("Stream reader error:", error);
    if (timerId) {
      clearInterval(timerId);
    }
    onError(error.message || "An error occurred while streaming.");
  }
};
